import React from 'react';
import SVG from 'react-inlinesvg';
import PropTypes from 'prop-types';
import PropTypesConfig from 'config/prop-types';
import { ThemeProvider } from 'styled-components';
import { __ } from '@wordpress/i18n';
import SearchIcon from 'styles/search.svg';
import CaretIcon from 'styles/caret.svg';
import SearchFilterLabel from 'components/searchFilterLabel';

// Styled components.
import theme from 'styles/theme';
import helperStyles from 'styles/helpers';
import styles from './styles';

import sendCustomEvent from '../../util/sendCustomEvent';

/**
 * A component to filter elections results.
 */
export default class SearchFilter extends React.PureComponent {
  /**
   * Define PropTypes for this component.
   * @type {object}
   */
  static propTypes = {
    results: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.shape(PropTypesConfig.MEASURE_SHAPE),
        PropTypes.shape(PropTypesConfig.RACE_SHAPE),
      ]),
    ),
    setSearchedTerm: PropTypes.func,
    widgetTitle: PropTypes.string,
    widgetId: PropTypes.string,
  };

  /**
   * Define default props for this component.
   * @type {object}
   */
  static defaultProps = {
    results: [],
    setSearchedTerm: () => {},
    widgetTitle: '',
    widgetId: '',
  };

  /**
   * Constructor.
   * @param {object} props - Props for this component.
   */
  constructor(props) {
    super(props);
    this.state = {
      searchTerm: '',
      displayFilterLabel: false,
      displayAutoComplete: false,
    };
  }

  /**
   * Handle user input on the search filter.
   *
   * @param {e} object - event object.
   */
  handleSearchTerm = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    this.setState({ searchTerm }, () => this.setDisplayAutoComplete());
  }

  /**
   * Return whether the searchTerm matches a 'result'.
   * It is a match when searchTerm is a substring of 'result' and it's also
   * the start of a word. Any character besides letters, numbers, or spaces is
   * stripped.
   *
   * @param {string} result - Result title or subtitle.
   *
   * @return {bool} True.  searchTerm matches result.
   *                False. searchTerm does not match result.
   */
  searchTermMatchResult = (result = '') => {
    const { searchTerm } = this.state;
    const resultStripped = result.replace(/[^a-z\d\s]+/gi, '');

    const searchTermStripped = searchTerm.replace(/[^a-z\d\s]+/gi, '');
    const subStringIndex = resultStripped
      ? searchTermStripped
        && resultStripped.toLowerCase().indexOf(searchTermStripped)
      : false;

    return (
      0 <= subStringIndex && (0 === subStringIndex
        || ' ' === resultStripped[subStringIndex - 1])
    );
  };

  /**
   * Return auto complete options based on the user input on the search filter.
   *
   * @return {array} auto complete options.
   */
  getAutoCompleteOptions = () => {
    const { results = [] } = this.props;
    const autoCompleteOptions = [];

    results.forEach(({ title, subtitle }) => {
      if (this.searchTermMatchResult(title)) {
        autoCompleteOptions.push(title);
      }

      if (this.searchTermMatchResult(subtitle)) {
        autoCompleteOptions.push(subtitle);
      }
    });

    results.forEach((race) => {
      /* eslint-disable camelcase */
      if (race.candidates) {
        race.candidates.forEach(({ full_name }) => {
          if (this.searchTermMatchResult(full_name)) {
            autoCompleteOptions.push(full_name);
          }
        });
      }
      /* eslint-enable camelcase */
    });

    return Array.from(new Set(autoCompleteOptions)); // Remove repeated elements
  }

  /**
   * Display auto complete options if the search term is not empty.
   */
  setDisplayAutoComplete = () => {
    const { searchTerm } = this.state;
    if (searchTerm) {
      this.setState({ displayAutoComplete: true });
    } else {
      this.setState({ displayAutoComplete: false });
    }
  }

  /**
   * Handle user click on a auto complete option.
   * It sets the clicked option on the parent component (ElectionHubWidget) to render
   * only the races that match with searched term.
   *
   * @param {string} searchedTerm option clicked by the user.
   */
  handleClickOnFilterOption = (searchedTerm) => {
    const { setSearchedTerm, widgetTitle, widgetId } = this.props;

    setSearchedTerm(searchedTerm);
    this.setState(
      {
        displayFilterLabel: true,
        searchTerm: searchedTerm,
      },
      () => {
        const { searchTerm } = this.state;

        sendCustomEvent({
          eventType: 'eHubApplyFilter',
          widgetId,
          widgetTitle,
          filteredCard: searchTerm,
        });
      }
    );
  }

  /**
   * Handle user click on any of the clear filter buttons.
   */
  handleClearFilter = () => {
    const { setSearchedTerm, widgetTitle, widgetId } = this.props;

    this.setState(
      {
        displayFilterLabel: false,
        searchTerm: '',
      }, () => {
        this.setDisplayAutoComplete();
        setSearchedTerm('');
        sendCustomEvent({
          eventType: 'eHubClearFilter',
          widgetId,
          widgetTitle,
        });
      }
    );
  };

  render() {
    const { Wrapper } = styles;
    const { displayAutoComplete, searchTerm, displayFilterLabel } = this.state;
    const { ButtonReset } = helperStyles;
    const autoCompleteOptions = this.getAutoCompleteOptions();

    return (
      <ThemeProvider theme={theme}>
        <Wrapper>
          <div className="election-hub__search-filter">
            <div className="election-hub__search-filter-searcher">
              {displayFilterLabel ? (
                <SearchFilterLabel
                  handleClearFilter={this.handleClearFilter}
                  text={searchTerm}
                />
              ) : (
                <input
                  type="text"
                  className="election-hub__search-filter--textfield"
                  placeholder={__('Filter Results', 'election-hub')}
                  onClick={() => this.setDisplayAutoComplete()}
                  onChange={this.handleSearchTerm}
                  maxLength="100"
                  value={searchTerm}
                />
              )}
              <div className="election-hub__search-filter--magnifying-glass">
                <SVG src={SearchIcon} />
              </div>
              {(displayAutoComplete && 0 !== autoCompleteOptions.length) && (
                <div
                  className="election-hub__search-filter--auto-complete-wrapper"
                >
                  {autoCompleteOptions.map((title) => (
                    <div
                      className="election-hub__search-filter--auto-complete-option" // eslint-disable-line max-len
                      onClick={() => this.handleClickOnFilterOption(title)}
                      onKeyDown={() => this.handleClickOnFilterOption(title)}
                      role="button"
                      tabIndex={0}
                      key={title}
                    >
                      <span>{title}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {0 < searchTerm.length && 0 === autoCompleteOptions.length && (
              <ButtonReset
                className="election-hub__search-filter-clear-filter"
                onClick={this.handleClearFilter}
                tabIndex={0}
                type="button"
              >
                { __('View all elections', 'election-hub')}
                <div className="election-hub__search-filter-clear--caret">
                  <SVG src={CaretIcon} />
                </div>
              </ButtonReset>
            )}
          </div>
          {(searchTerm && 0 === autoCompleteOptions.length && ! displayFilterLabel) && ( // eslint-disable-line max-len
            <span className="election-hub__search-filter-no-match">
              {
                __(
                  // eslint-disable-next-line max-len
                  `No races match "${searchTerm}". Try another search query above.`,
                  'election-hub'
                )
              }
            </span>
          )}
        </Wrapper>
      </ThemeProvider>
    );
  }
}
